/**
 * Rank (1.K-2 deliverable 10; baseline section 6 items 6 and 7): one in-world order for every
 * placed, non-retired placement. A priority topological sort over the order graph of the
 * evaluated constraints in status `active` or `lag_unresolved` (dropped, contradicted and
 * unevaluated ones left out), each strongly connected component (only placement arcs and merges
 * can form one) collapsed to one node. A placement is ranked where its start edge is emitted, so P
 * ranks before Q whenever a path from P's start to Q's start holds a `before` or `during` arc; a
 * path of merges alone leaves them in one node, in key order.
 *
 * Keys: `boundLo`, then `boundHi` (null last), then coarser precision first (null last), then
 * `orderKey` and `id` by code unit. A placement without a lower bound takes the smallest key among
 * the start nodes reachable from it, with a flag that sorts it just before that key; with none it
 * has no key. A node's key is the smallest key among the keyed start edges merged into it; a node
 * with none is emitted as soon as it is available. Among available nodes the smallest key goes
 * first, then (keyless nodes) the smallest edge.
 */
import { PRECISIONS } from '@codex/contracts';
import { byCodeUnit } from './input.js';
import { copyInt32, int32, int32Unset, uint8 } from './pool.js';
import { components, identity, MinHeap, toCsr } from './scc.js';
const PRECISION_INDEX = new Map(PRECISIONS.map((p, i) => [p, i]));
/** A precision's place in `PRECISIONS` (coarsest first), past the end for none. */
export const precisionPlace = (p) => p === null ? PRECISIONS.length : PRECISION_INDEX.get(p);
/** The ranked placements' indexes in rank order. */
export function rankOrder(g) {
    const { ids, orderKeys, lo, hi, precision, ranked, tails, heads } = g;
    const count = ids.length;
    const n = 2 * count;
    const m = tails.length;
    /** `boundHi`, then precision, then `orderKey`, then `id`. */
    const compareTail = (x, y) => {
        const hx = hi[x];
        const hy = hi[y];
        if (hx !== hy)
            return hx < hy ? -1 : 1;
        return (precision[x] - precision[y] ||
            byCodeUnit(orderKeys[x], orderKeys[y]) ||
            byCodeUnit(ids[x], ids[y]));
    };
    const out = toCsr(n, tails, identity(m));
    const { comp, count: comps } = components(out, heads);
    // 1. The placements with a lower bound, in key order: their place is the anchor of a key.
    const bounded = [];
    for (let p = 0; p < count; p++) {
        if (ranked[p] === 1 && !Number.isNaN(lo[p]))
            bounded.push(p);
    }
    bounded.sort((x, y) => {
        const lx = lo[x];
        const ly = lo[y];
        return lx < ly ? -1 : lx > ly ? 1 : compareTail(x, y);
    });
    const anchor = int32(count, -1);
    const flag = uint8(count);
    for (let i = 0; i < bounded.length; i++) {
        const p = bounded[i];
        anchor[p] = i;
        flag[p] = 1;
    }
    // 2. The smallest anchor reachable from each component, in reverse topological order (Tarjan
    // numbers the sinks first, so every successor of component k has a smaller number).
    const NONE = count;
    const reach = int32(comps, NONE);
    for (let i = 0; i < bounded.length; i++) {
        const k = comp[2 * bounded[i]];
        if (i < reach[k])
            reach[k] = i;
    }
    const members = toCsr(comps, comp, identity(n));
    for (let k = 0; k < comps; k++) {
        let best = reach[k];
        for (let i = members.first[k]; i < members.first[k + 1]; i++) {
            const v = members.arcs[i];
            for (let j = out.first[v]; j < out.first[v + 1]; j++) {
                const r = reach[comp[heads[out.arcs[j]]]];
                if (r < best)
                    best = r;
            }
        }
        reach[k] = best;
    }
    // 3. Every ranked placement in key order, (anchor, flag, the rest), without one big sort: the
    // keyless ones first, then for each anchor the placements that take it, then the anchor itself.
    const keyless = [];
    const taking = int32(bounded.length + 1);
    for (let p = 0; p < count; p++) {
        if (ranked[p] === 0 || flag[p] === 1)
            continue;
        const r = reach[comp[2 * p]];
        if (r === NONE)
            keyless.push(p);
        else {
            anchor[p] = r;
            taking[r + 1] = taking[r + 1] + 1;
        }
    }
    for (let i = 0; i < bounded.length; i++) {
        taking[i + 1] = taking[i + 1] + taking[i];
    }
    const takers = int32Unset(taking[bounded.length]);
    const fill = copyInt32(taking.subarray(0, bounded.length));
    for (let p = 0; p < count; p++) {
        if (ranked[p] === 0 || flag[p] === 1 || anchor[p] < 0)
            continue;
        const r = anchor[p];
        takers[fill[r]] = p;
        fill[r] = fill[r] + 1;
    }
    const order = keyless.length > 1 ? keyless.sort(compareTail) : keyless;
    for (let i = 0; i < bounded.length; i++) {
        const from = taking[i];
        const to = taking[i + 1];
        if (to - from === 1)
            order.push(takers[from]);
        else if (to - from > 1) {
            for (const p of [...takers.subarray(from, to)].sort(compareTail))
                order.push(p);
        }
        order.push(bounded[i]);
    }
    // 4. Each component's placements in key order, and its own key: the place of its first keyed
    // placement; a component without one is keyless and goes first, by its smallest edge.
    const place = int32(count);
    const starts = int32Unset(order.length);
    for (let i = 0; i < order.length; i++) {
        const p = order[i];
        place[p] = i;
        starts[i] = 2 * p;
    }
    const byComp = toCsr(comps, comp, starts);
    const priority = int32(comps);
    for (let k = 0; k < comps; k++) {
        priority[k] = members.arcs[members.first[k]];
        for (let i = byComp.first[k]; i < byComp.first[k + 1]; i++) {
            const p = byComp.arcs[i] >> 1;
            if (anchor[p] >= 0) {
                priority[k] = n + place[p];
                break;
            }
        }
    }
    // 5. The priority topological sort over the components.
    const indegree = int32(comps);
    for (let a = 0; a < m; a++) {
        const kt = comp[tails[a]];
        const kh = comp[heads[a]];
        if (kt !== kh)
            indegree[kh] = indegree[kh] + 1;
    }
    const heap = new MinHeap(n + count);
    // Priorities are distinct: an edge below n for a keyless component, n + a place otherwise.
    const compAt = int32(n + count);
    for (let k = 0; k < comps; k++) {
        compAt[priority[k]] = k;
        if (indegree[k] === 0)
            heap.push(priority[k]);
    }
    const result = int32Unset(order.length);
    let next = 0;
    while (heap.size > 0) {
        const k = compAt[heap.pop()];
        for (let i = byComp.first[k]; i < byComp.first[k + 1]; i++) {
            result[next++] = byComp.arcs[i] >> 1;
        }
        for (let i = members.first[k]; i < members.first[k + 1]; i++) {
            const v = members.arcs[i];
            for (let j = out.first[v]; j < out.first[v + 1]; j++) {
                const kh = comp[heads[out.arcs[j]]];
                if (kh === k)
                    continue;
                indegree[kh] = indegree[kh] - 1;
                if (indegree[kh] === 0)
                    heap.push(priority[kh]);
            }
        }
    }
    return result;
}
//# sourceMappingURL=rank.js.map